import { useNavigate, useSearchParams } from 'react-router';
import type { ContentCategory, ContentListItem } from '../../api/types';
import { useContentList } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { IconLock } from '../../components/icons';
import { useTranslation } from '../../i18n/I18nProvider';
import { formatDate } from '../../lib/format';
import { Badge, SegmentedControl, SkeletonList } from '../../ui';

export const CONTENT_CATEGORIES: readonly ContentCategory[] = [
  'nutrition',
  'training',
  'recovery',
  'mobility',
  'lifestyle',
];

const CATEGORY_KEY: Record<ContentCategory, string> = {
  nutrition: 'education.categoryNutrition',
  training: 'education.categoryTraining',
  recovery: 'education.categoryRecovery',
  mobility: 'education.categoryMobility',
  lifestyle: 'education.categoryLifestyle',
};

export function categoryLabelKey(category: ContentCategory): string {
  return CATEGORY_KEY[category] ?? 'education.title';
}

function isCategory(value: string | null): value is ContentCategory {
  return value !== null && (CONTENT_CATEGORIES as readonly string[]).includes(value);
}

/**
 * Edukacijski sadržaj.
 *
 * Zaključani članci se VIDE na popisu, s lokotom — to je namjerno: korisnik mora
 * znati da nešto postoji, inače ne zna ni zašto bi platio. Tijelo i datoteka
 * ostaju na serveru dok pristup nije potvrđen.
 * Kategorija živi u URL-u (`?kategorija=`), pa je odabir dijeljiv i preživi refresh.
 */
export function Education() {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const raw = params.get('kategorija');
  const category = isCategory(raw) ? raw : undefined;
  const query = useContentList(category);

  function selectCategory(next: string) {
    const p = new URLSearchParams(params);
    if (next === 'all') p.delete('kategorija');
    else p.set('kategorija', next);
    setParams(p, { replace: true });
  }

  return (
    <>
      <PageHeader title={t('education.title')} subtitle={t('education.subtitle')} />

      <SegmentedControl
        className="mb-5"
        ariaLabel={t('education.filter')}
        value={category ?? 'all'}
        onChange={selectCategory}
        options={[
          { value: 'all', label: t('education.categoryAll') },
          ...CONTENT_CATEGORIES.map((c) => ({ value: c, label: t(categoryLabelKey(c)) })),
        ]}
      />

      <QueryBoundary
        query={query}
        skeleton={<SkeletonList rows={4} />}
        isEmpty={(d) => d.items.length === 0}
        empty={{ message: t('education.empty') }}
      >
        {(data) => (
          <ul className="grid gap-3 md:grid-cols-2" data-testid="education-list">
            {data.items.map((item) => (
              <li key={item.slug}>
                <ArticleRow item={item} onOpen={() => void navigate(`/edukacija/${item.slug}`)} locale={locale} />
              </li>
            ))}
          </ul>
        )}
      </QueryBoundary>
    </>
  );
}

function ArticleRow({
  item,
  onOpen,
  locale,
}: {
  item: ContentListItem;
  onOpen: () => void;
  locale: 'hr' | 'en';
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onOpen}
      data-locked={item.locked}
      className="flex w-full flex-col items-start gap-2 rounded-xl border border-line-subtle bg-surface p-5 text-left shadow-card transition-colors hover:border-line"
    >
      <span className="flex w-full items-start justify-between gap-3">
        <span className="eyebrow">{t(categoryLabelKey(item.category))}</span>
        {item.locked ? (
          <Badge tone="neutral" className="gap-1.5">
            <IconLock width={13} height={13} />
            {t('education.locked')}
          </Badge>
        ) : null}
      </span>
      <span className="font-display text-[19px] font-semibold leading-tight text-ink">
        {item.title}
      </span>
      {item.publishedAt && (
        <span className="text-[12px] text-ink-muted">{formatDate(item.publishedAt, locale)}</span>
      )}
      <span className="mt-1 text-[13px] text-accent-dark underline">
        {item.locked ? t('education.seeWhatsInside') : t('education.read')}
      </span>
    </button>
  );
}
